// dashboard/src/components/Interact/RecordScreen.tsx
import { deleteObject } from 'firebase/storage';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import RecordRTC from 'recordrtc';
import { v4 as uuidv4 } from "uuid";
import colors from "../../constants/colors";
import { BrowserInfo } from '../../constants/types';
import { useFirebase } from "../../contexts/firebase";
// @ts-ignore - module resolution is environment-specific in this workspace
// eslint-disable-next-line import/no-unresolved
import {
  analyzeBlobSignal,
  classifyMediaError,
  createSignalMonitor,
  getTrackDiagnostics,
  watchAudioTrackMute,
  SILENCE_PEAK_THRESHOLD,
  LOW_PEAK_THRESHOLD,
  type SignalMonitor,
  type TrackDiagnostics,
} from '../../helpers/audioDiagnostics';
import { getBrowserPlatformInfo } from "../../helpers/detectBrowser";
import MediaWindow from './MediaWindow';
import MicTestModal from './MicTestModal';
import VideoPreview from './VideoPreview';

// Structured record of what happened during a capture. Emit this to your
// backend (see onDiagnostics) so "some customers" becomes a queryable pattern:
// on managed Windows you'll see muteFired=true / a redirected device label /
// maxLiveRms ~0, which is exactly what IT confirmed (policy or competing app
// holding the mic). University machines won't show it.
export interface CaptureDiagnostics {
  ok: boolean;
  reason: 'ok' | 'silent' | 'quiet' | 'too_long' | 'decode_failed';
  browser: BrowserInfo['browser'];
  platform: BrowserInfo['platform'];
  // Raw UA string. detectBrowser buckets managed/legacy Edge as 'unknown', so on
  // corporate Edge fleets the parsed fields lie — keep the raw string for triage.
  userAgent: string;
  blobSizeBytes: number;
  contentType: string;
  durationSec: number | null;
  peak: number | null;
  rms: number | null;
  maxLiveRms: number | null; // loudest the live mic got while recording
  muteFired: boolean; // did the audio track go browser-muted mid-take?
  tracks: TrackDiagnostics[];
  decodeError: string | null;
}

interface RecordScreenProps {
  onAnalyze: (audioUrl: string) => void;
  isPhoneMode?: boolean; // audio-only mode based on prompt type
  videoRecordingEnabled?: boolean; // persistent per-account preference; default true
  onVideoRecordingEnabledChange?: (next: boolean) => void;
  onDiagnostics?: (d: CaptureDiagnostics) => void; // optional telemetry hook
}

const MAX_DURATION = 59;

// Where the "copy these details" report is addressed. Change to your real inbox.
const SUPPORT_EMAIL = 'support@interflexion.com';

// Live RMS from createSignalMonitor is smaller than a decoded peak; treat a
// recording whose loudest live moment never crossed this as "mic produced nothing."
const LIVE_FLAT_THRESHOLD = 0.006;

const extensionForMime = (mime: string): string => {
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('wav')) return 'wav';
  if (mime.includes('mp4') || mime.includes('mpeg') || mime.includes('aac') || mime.includes('m4a')) return 'm4a';
  return 'webm';
};

const RecordScreen = ({
  onAnalyze,
  isPhoneMode = false,
  videoRecordingEnabled = true,
  onVideoRecordingEnabledChange,
  onDiagnostics,
}: RecordScreenProps) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioRecorder, setAudioRecorder] = useState<RecordRTC | null>();
  const [videoRecorder, setVideoRecorder] = useState<RecordRTC | null>();
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recordedVideo, setRecordedVideo] = useState<{ blob: Blob; url: string } | null>(null);
  const [audioTooLong, setAudioTooLong] = useState(false);
  const [liveLevel, setLiveLevel] = useState(0); // 0..1, drives the little meter
  const [captureError, setCaptureError] = useState<string | null>(null);
  // Why the last getUserMedia failed, kept separate from captureError so a
  // successful re-acquire (e.g. after the mic test closes) can clear it.
  const [streamError, setStreamError] = useState<string | null>(null);
  const [lastDiagnostics, setLastDiagnostics] = useState<CaptureDiagnostics | null>(null);
  const [micTestOpen, setMicTestOpen] = useState(false);

  const [fileProcessingStatus, setFileProcessingStatus] =
    useState<'idle' | 'uploading' | 'verifying' | 'ready' | 'error'>('idle');

  const firebase = useFirebase();
  const [browserInfo] = useState<BrowserInfo>(getBrowserPlatformInfo());

  // WORKAROUND for an apparent iOS Safari quirk: the very first MediaRecorder
// encode of a freshly-acquired video track has been observed to produce a
// blob with an inconsistent rotation transform (wrong on the poster frame,
// then "corrected" but visually squished once playback re-negotiates it),
// while every later encode of that same already-used track is correct.
// Record and immediately discard a short throwaway clip right after the
// stream is acquired, so the user's real first take is never actually the
// track's first encode. This is a mitigation for a suspected platform bug,
// not a real fix — if it doesn't hold up, this theory is wrong too and
// should come back out rather than linger as unexplained dead weight.
const warmUpTrack = (mediaStream: MediaStream) => {
  try {
    const warmupRecorder = new RecordRTC(mediaStream, {
      type: 'video',
      recorderType: RecordRTC.MediaStreamRecorder,
      mimeType: 'video/webm;codecs=vp8',
    });
    warmupRecorder.startRecording();
    setTimeout(() => {
      warmupRecorder.stopRecording(() => {
        warmupRecorder.destroy();
      });
    }, 300);
  } catch (error) {
    console.error('Warm-up recording failed (non-fatal):', error);
  }
};

  // Stable id for this component's recording; the final key extension is chosen
  // at upload time from the real blob type (Chrome/Android -> webm, Safari -> m4a),
  // fixing the old bug where WAV/Opus data was written to a ".mp4" key.
  const recordingIdRef = useRef<string>(uuidv4());

  // Refs so async stop callbacks and cleanup don't fight React state.
  const streamRef = useRef<MediaStream | null>(null);
  const signalMonitorRef = useRef<SignalMonitor | null>(null);
  const detachMuteRef = useRef<(() => void) | null>(null);
  const muteFiredRef = useRef<boolean>(false);
  const clonedAudioTrackRef = useRef<MediaStreamTrack | null>(null);
  const meterIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const uploadedPathRef = useRef<string | null>(null);
  // Mirrors of recorder/playback state for the unmount cleanup, whose closure
  // would otherwise only ever see the initial (null) values.
  const audioRecorderRef = useRef<RecordRTC | null>(null);
  const videoRecorderRef = useRef<RecordRTC | null>(null);
  const recordedVideoUrlRef = useRef<string | null>(null);
  audioRecorderRef.current = audioRecorder ?? null;
  videoRecorderRef.current = videoRecorder ?? null;
  recordedVideoUrlRef.current = recordedVideo?.url ?? null;

  // getUserMedia is async: the user can navigate away (or toggle video, or open
  // the mic test) before it resolves. Each acquisition takes a sequence number;
  // a stream that resolves after it has been superseded or after unmount is
  // stopped on arrival instead of being stored, so the camera/mic never stays on.
  const isMountedRef = useRef<boolean>(true);
  const acquireSeqRef = useRef<number>(0);

  const stopTracks = (s: MediaStream) => s.getTracks().forEach((t) => t.stop());

  const releaseStream = () => {
    acquireSeqRef.current++; // invalidate any in-flight getStream()
    if (streamRef.current) {
      stopTracks(streamRef.current);
      streamRef.current = null;
      if (isMountedRef.current) setStream(null);
    }
    clonedAudioTrackRef.current?.stop();
    clonedAudioTrackRef.current = null;
  };

  // Two independent axes collapse into one: isPhoneMode forces audio-only
  // regardless of preference; otherwise the user's own toggle decides.
  const audioOnly = isPhoneMode || !videoRecordingEnabled;

  const getStream = async () => {
    const seq = ++acquireSeqRef.current;
    const isStale = () => !isMountedRef.current || seq !== acquireSeqRef.current;
    try {
      if ('mediaDevices' in navigator === false) {
        alert('Sorry, your browser does not support media devices.');
        return;
      }

      const isIOS = browserInfo?.platform === 'ios';

      const videoConstraints = {
        audio: true,
        video: {
          facingMode: 'user',
          ...(isIOS && {
            width: { ideal: 480, max: 640 },
            height: { ideal: 640, max: 848 },
            aspectRatio: { ideal: 0.75 }
          })
        }
      };

      // Phone mode never needs the camera, and neither does a user who has
      // opted out of video; asking for it only invites more permission
      // friction and a wasted video track.
      const constraints = audioOnly ? { audio: true } : videoConstraints;
      let mediaStream: MediaStream;
      let cameraRefused = false;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        if (audioOnly) throw err;
        // An audio+video request fails wholesale if either device is refused
        // (e.g. Windows blocks only the camera). Retry the mic alone so a
        // camera problem doesn't masquerade as a mic problem; if this throws
        // too, the outer catch classifies the mic's own error.
        console.warn('[capture] audio+video request failed, retrying audio only', err);
        mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        cameraRefused = true;
      }
      if (isStale()) {
        stopTracks(mediaStream);
        return;
      }
      setStreamError(null);

      if (cameraRefused) {
        alert('Your camera is not available. Recording audio only.');
        if (streamRef.current) stopTracks(streamRef.current);
        setStream(mediaStream);
        streamRef.current = mediaStream;
      } else if (!audioOnly && mediaStream.getVideoTracks().length === 0) {
        stopTracks(mediaStream); // don't leave its audio track running
        alert('Video recording not available. Trying with audio only.');
        const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (isStale()) {
          stopTracks(audioStream);
          return;
        }
        if (streamRef.current) stopTracks(streamRef.current);
        setStream(audioStream);
        streamRef.current = audioStream;
      } else {
        if (streamRef.current) stopTracks(streamRef.current);
        setStream(mediaStream);
        streamRef.current = mediaStream;
        if (isIOS && !audioOnly) {
          warmUpTrack(mediaStream);
        }
      }
    } catch (error) {
      console.error(error);
      const classified = await classifyMediaError(error);
      if (isStale()) return;
      console.warn(`[capture] getUserMedia failed (${classified.kind}): ${classified.detail}`);
      setStreamError(classified.message);
    }
  };

  useEffect(() => {
    // Stop and release whatever stream is currently open (e.g. from a prior
    // audioOnly value) before requesting a new one under new constraints.
    releaseStream();
    getStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioOnly]);

  const startRecording = () => {
    try {
      const activeStream = streamRef.current;
      if (!activeStream) {
        alert(
          streamError ??
            'No media stream available. Is your microphone in use by another application?'
        );
        return;
      }

      cleanupPreviousRecording();

      // Reset state for a fresh take.
      setRecordedVideo(null);
      setAudioBlob(null);
      setAudioTooLong(false);
      setCaptureError(null);
      setFileProcessingStatus('idle');
      muteFiredRef.current = false;

      // Start watching for the audio track being seized mid-take. On managed
      // Windows this is the tell: the track fires 'mute' and every subsequent
      // sample is zero, producing a full-length silent Opus file.
      detachMuteRef.current?.();
      detachMuteRef.current = watchAudioTrackMute(activeStream, (event) => {
        if (event === 'mute') muteFiredRef.current = true;
        console.warn(`[capture] audio track ${event}`);
      });

      // Live level monitor: powers the on-screen meter and the maxRms verdict.
      signalMonitorRef.current?.stop();
      const monitor = createSignalMonitor(activeStream);
      signalMonitorRef.current = monitor;
      if (meterIntervalRef.current) clearInterval(meterIntervalRef.current);
      meterIntervalRef.current = setInterval(() => setLiveLevel(monitor.getCurrentRms()), 120);

      // Give the audio recorder its OWN cloned audio track so it never contends
      // with the video recorder over one shared track. (Removes the failure mode
      // that was genuinely our code's fault; it does not fix an environmentally
      // silent mic — nothing client-side can.)
      // A clone is a live mic capture in its own right; stop the previous take's
      // before replacing it, or it keeps the mic open after the screen is left.
      clonedAudioTrackRef.current?.stop();
      const sourceAudioTrack = activeStream.getAudioTracks()[0];
      const audioClone = sourceAudioTrack.clone();
      clonedAudioTrackRef.current = audioClone;
      const audioOnlyStream = new MediaStream([audioClone]);

      const audioRecordRTC = new RecordRTC(audioOnlyStream, { type: 'audio' });
      setAudioRecorder(audioRecordRTC);
      audioRecordRTC.startRecording();

      // Only record video when we're actually in video mode and have a camera.
      if (!audioOnly && activeStream.getVideoTracks().length > 0) {
        try {
          // Must declare an audio codec alongside vp8, or Firefox's MediaRecorder
          // throws outright on a stream that also carries an audio track (Chrome
          // is lenient and fills in a default; Firefox is not). That throw used to
          // land in the outer catch below, which fired AFTER the audio recorder
          // above had already started on its own cloned track — the crash here
          // doesn't touch that clone, but leaving it uncaught up there meant a
          // video-only failure could mask a perfectly good audio capture in
          // progress. Isolate it so video failures never affect the audio path.
          const videoRecordRTC = new RecordRTC(activeStream, {
            type: 'video',
            recorderType: RecordRTC.MediaStreamRecorder,
            // RecordRTC's type defs only list a handful of literal mimeType
            // strings and don't include the audio+video pairing MediaRecorder
            // actually supports; the runtime just forwards this to
            // MediaRecorder's constructor, so the string itself is valid.
            mimeType: 'video/webm;codecs=vp8,opus' as any,
          });
          setVideoRecorder(videoRecordRTC);
          videoRecordRTC.startRecording();
        } catch (videoError) {
          console.error('Video recorder failed to start; continuing audio-only.', videoError);
          setVideoRecorder(null);
        }
      } else {
        setVideoRecorder(null);
      }
    } catch (error) {
      console.error(error);
      setCaptureError('Could not start recording. Please reload and try again.');
    }
  };

  const cleanupPreviousRecording = async () => {
    const path = uploadedPathRef.current;
    if (path && (fileProcessingStatus === 'ready' || fileProcessingStatus === 'error')) {
      try {
        await deleteObject(firebase.storage.ref(path));
        console.log('Previous recording deleted for privacy');
      } catch (error) {
        console.log('No previous file to delete:', error);
      }
    }
  };

    const openMicTest = () => {
    stopMonitoring();
    releaseStream();
    setMicTestOpen(true);
  };

  const closeMicTest = () => {
    setMicTestOpen(false);
    setCaptureError(null);
    getStream(); // re-acquire the production stream for another attempt
  };

  const stopMonitoring = () => {
    if (meterIntervalRef.current) {
      clearInterval(meterIntervalRef.current);
      meterIntervalRef.current = null;
    }
    signalMonitorRef.current?.stop();
    signalMonitorRef.current = null;
    detachMuteRef.current?.();
    detachMuteRef.current = null;
    setLiveLevel(0);
  };

  const stopRecording = () => {
    if (!audioRecorder) return;

    audioRecorder.stopRecording(async () => {
      const blob = audioRecorder.getBlob();
      // The clone only exists to feed this recorder; release it now the take is done.
      clonedAudioTrackRef.current?.stop();
      clonedAudioTrackRef.current = null;
      await finalizeAudio(blob);
      setAudioRecorder(null);
    });

    if (videoRecorder) {
      videoRecorder.stopRecording(() => {
        const recordedVideoBlob = videoRecorder.getBlob();
        const recordedVideoURL = URL.createObjectURL(recordedVideoBlob);
        setRecordedVideo({ blob: recordedVideoBlob, url: recordedVideoURL });
        setVideoRecorder(null);
      });
    }
  };

  // Decides whether the take is usable and either uploads it or blocks it with
  // an actionable message. This is where silent files stop reaching the server.
  const finalizeAudio = async (blob: Blob) => {
    const maxLiveRms = signalMonitorRef.current ? signalMonitorRef.current.getMaxRms() : null;
    const tracks = streamRef.current ? getTrackDiagnostics(streamRef.current) : [];
    const muteFired = muteFiredRef.current;
    stopMonitoring();

    setAudioBlob(blob);

    let durationSec: number | null = null;
    let peak: number | null = null;
    let rms: number | null = null;
    let decodeError: string | null = null;

    try {
      const signal = await analyzeBlobSignal(blob);
      durationSec = signal.durationSec;
      peak = signal.peak;
      rms = signal.rms;
      console.log(`Recording: ${durationSec.toFixed(2)}s, peak ${peak.toFixed(4)}, rms ${rms.toFixed(4)}`);
    } catch (err) {
      // Do NOT swallow this and upload anyway (the original bug). An undecodable
      // blob is a failed capture.
      decodeError = err instanceof Error ? err.message : String(err);
      console.error('Audio decode failed:', decodeError);
    }

    const emit = (reason: CaptureDiagnostics['reason'], ok: boolean) => {
      const diagnostics: CaptureDiagnostics = {
        ok,
        reason,
        browser: browserInfo.browser,
        platform: browserInfo.platform,
        userAgent: navigator.userAgent,
        blobSizeBytes: blob.size,
        contentType: blob.type || 'unknown',
        durationSec,
        peak,
        rms,
        maxLiveRms,
        muteFired,
        tracks,
        decodeError,
      };
      onDiagnostics?.(diagnostics);
      console.log('[capture diagnostics]', diagnostics);
      setLastDiagnostics(diagnostics);
    };

    // 1. Too long — unchanged behavior, but now we also refuse to upload it.
    if (durationSec != null && durationSec > MAX_DURATION) {
      setAudioTooLong(true);
      setFileProcessingStatus('error');
      setCaptureError(
        `That was about ${Math.round(durationSec)} seconds. Please keep your response under a minute and record again.`
      );
      emit('too_long', false);
      return;
    }

    // 2. Silence — the corporate-Windows failure. Block and explain.
    const recordedSilent =
      decodeError != null || blob.size < 1024 || (peak != null && peak < SILENCE_PEAK_THRESHOLD);
    const liveFlat = maxLiveRms != null && maxLiveRms < LIVE_FLAT_THRESHOLD;
    const audioTrackHeld =
      muteFired || tracks.some((t) => t.kind === 'audio' && (t.muted || !t.enabled));

    if (recordedSilent) {
      setFileProcessingStatus('error');
      if (audioTrackHeld || liveFlat) {
        setCaptureError(
          "We couldn't hear your microphone. Another app or your organization's security settings " +
            'may be using it. Close apps like Teams, Zoom, or Webex, confirm the right microphone is ' +
            'selected and unmuted in your system settings, then record again.'
        );
      } else {
        setCaptureError(
          "The recording came out silent. Check that the correct microphone is selected and unmuted, " +
            'then record again. If it keeps happening, contact your IT support — microphone access may be restricted.'
        );
      }
      emit(decodeError ? 'decode_failed' : 'silent', false);
      return;
    }

    // 3. Quiet but usable — allow, but note it (helps explain weak analyses later).
    if (peak != null && peak < LOW_PEAK_THRESHOLD) {
      emit('quiet', true);
    } else {
      emit('ok', true);
    }

    await uploadAudio(blob);
  };

  const uploadAudio = async (blob: Blob) => {
    const ext = extensionForMime(blob.type || '');
    const path = `interact/_${recordingIdRef.current}.${ext}`;
    uploadedPathRef.current = path;
    const audioRef = firebase.storage.ref(path);

    try {
      setFileProcessingStatus('uploading');

      // Set contentType explicitly rather than relying on blob.type flowing
      // through, and keep the key extension consistent with the bytes.
      await firebase.storage.uploadBytes(audioRef, blob, {
        contentType: blob.type || 'audio/webm',
      });

      const fileSizeMB = blob.size / (1024 * 1024);
      if (fileSizeMB > 0.2) {
        const delay = Math.min(3000, 500 + (fileSizeMB * 1000));
        console.log(`File size: ${fileSizeMB.toFixed(1)}MB, waiting ${delay}ms for propagation...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      setFileProcessingStatus('ready');
    } catch (err: any) {
      console.error("File processing failed:", err?.message || err);
      setFileProcessingStatus('error');
      setCaptureError(`Upload failed: ${err?.message || 'please try again.'}`);
    }
  };

  const handleSubmit = async () => {
    const path = uploadedPathRef.current;
    if (audioBlob && path && fileProcessingStatus === 'ready') {
      try {
        setLoading(true);
        setResult('');

        releaseStream();

        onAnalyze(path);
      } catch (err: any) {
        console.error("Analysis submission failed:", err);
        setCaptureError("Failed to start analysis. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  // Playback URL for the audio-only take; revoked when the blob is replaced or on unmount.
  useEffect(() => {
    if (!audioBlob) { setAudioUrl(null); return; }
    const url = URL.createObjectURL(audioBlob);
    setAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [audioBlob]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopMonitoring();
      try { audioRecorderRef.current?.destroy(); } catch { /* noop */ }
      try { videoRecorderRef.current?.destroy(); } catch { /* noop */ }
      releaseStream();
      if (recordedVideoUrlRef.current) {
        URL.revokeObjectURL(recordedVideoUrlRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canAnalyze = audioBlob && !audioTooLong && fileProcessingStatus === 'ready' && !loading;

  const getAnalyzeButtonText = () => {
    if (loading) return 'Analyzing...';
    if (audioTooLong) return 'Recording Too Long';
    if (fileProcessingStatus === 'uploading') return 'Uploading...';
    if (fileProcessingStatus === 'verifying') return 'Verifying Upload...';
    if (fileProcessingStatus === 'error') return 'Recording Failed';
    if (fileProcessingStatus === 'ready') return 'Analyze';
    return 'Processing...';
  };

  const isRecording = !!audioRecorder;

  const getAudioStatusText = () => {
    if (isRecording) return 'Recording...';
    if (!audioBlob) return 'Ready to record';
    if (audioTooLong || fileProcessingStatus === 'error') return 'Please record again';
    if (fileProcessingStatus === 'ready') return 'Ready to analyze';
    return 'Processing recording...';
  };

  const toggleDisabled =
    isRecording ||
    fileProcessingStatus === 'uploading' ||
    fileProcessingStatus === 'verifying' ||
    loading;
  const meterPct = Math.min(100, Math.round(liveLevel * 300));

  const LevelMeter = () => (
    <div style={{ width: '80%', maxWidth: 320, marginTop: 16 }}>
      <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 4, textAlign: 'center' }}>
        Mic level
      </div>
      <div style={{ height: 10, background: '#e9ecef', borderRadius: 5, overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: `${meterPct}%`,
          background: meterPct > 6 ? '#28a745' : '#adb5bd',
          transition: 'width 100ms linear',
        }} />
      </div>
    </div>
  );

  // Explicit width matters here: this component is used inside flex columns
  // with align-items:center (not stretch), so a plain auto-width div would
  // shrink-wrap to its narrowest content line instead of filling the column
  // — and every 100%-width child below (MediaWindow, the record buttons)
  // would inherit that same collapsed basis.
  return (
    <div className="wrapper" style={{ width: '100%' }}>
      {/* Phone Mode / audio-only preference: static mic with recording indicator */}
      {!recordedVideo && audioOnly && (
        <MediaWindow
          portraitAspectRatio="3/4"
          landscapeAspectRatio="16/9"
          maxWidth="600px"
          backgroundColor={colors.OFF_BRAND_INTERACT_BLUE}
        >
          <div className="phone-mode-container" style={{
            width: '100%', height: '100%', boxSizing: 'border-box', padding: '20px',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}>
            {/* flex:1 + minHeight:0 lets this area shrink to whatever room the
                box's aspect ratio leaves, instead of the fixed circle below
                overflowing a short box and getting clipped by MediaWindow. */}
            <div style={{
              flex: '1 1 0%', minHeight: 0, width: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{
                // height + max-width (with aspect-ratio deriving the other axis)
                // is the plain-CSS equivalent of object-fit:contain for a div:
                // the circle scales UP or DOWN with whichever of the wrapper's
                // two dimensions is tighter, instead of pinning to a fixed px
                // cap that looks stranded once the window is bigger than it.
                height: '65%', maxWidth: '65%', aspectRatio: '1 / 1', borderRadius: '50%',
                backgroundColor: isRecording ? '#17a2b8' : '#6c757d',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.3s ease',
                boxShadow: isRecording ? '0 0 30px rgba(23, 162, 184, 0.5)' : 'none'
              }}>
                <svg width="40%" height="40%" viewBox="0 0 24 24" fill="none" stroke="white"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ animation: isRecording ? 'pulse 1.5s ease-in-out infinite' : 'none' }}>
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                  <line x1="12" y1="19" x2="12" y2="23"></line>
                  <line x1="8" y1="23" x2="16" y2="23"></line>
                </svg>
              </div>
            </div>
            <p style={{ marginTop: '12px', fontSize: '18px', color: '#6c757d', textAlign: 'center' }}>
              {getAudioStatusText()}
            </p>
            {isRecording && <LevelMeter />}
            {audioUrl && !isRecording && (
              <audio src={audioUrl} controls style={{ width: '80%', maxWidth: 320, marginTop: 12 }} />
            )}
            <style>{`@keyframes pulse { 0%,100%{transform:scale(1);} 50%{transform:scale(1.1);} }`}</style>
          </div>
        </MediaWindow>
      )}

      {/* Video Mode: live preview */}
      {!recordedVideo && !audioOnly && (
        <div className="video-wrap">
          <MediaWindow backgroundColor={colors.OFF_BRAND_INTERACT_BLUE} maxWidth="600px">
            <VideoPreview stream={stream} />
          </MediaWindow>
          {isRecording && (
            <div style={{ display: 'flex', justifyContent: 'center' }}><LevelMeter /></div>
          )}
        </div>
      )}

      {/* Recorded video playback */}
      {recordedVideo && !audioOnly && (
        <div className="recorded-video-container" style={{
          width: '100%', maxWidth: '600px', margin: '0 auto', padding: '0 10px'
        }}>
          <MediaWindow backgroundColor="#000" borderRadius="8px">
            <video src={recordedVideo.url} controls style={{
              width: '100%', height: '100%', objectFit: 'contain',
              ...((browserInfo?.platform === 'ios') && { transformOrigin: 'center center' })
            }} />
          </MediaWindow>
        </div>
      )}

      {/* Recorded confirmation in phone mode / audio-only preference */}
      {recordedVideo && audioOnly && (
        <MediaWindow
          portraitAspectRatio="3/4"
          landscapeAspectRatio="16/9"
          maxWidth="600px"
          backgroundColor={colors.OFF_BRAND_INTERACT_BLUE}
        >
          <div className="phone-mode-recorded" style={{
            width: '100%', height: '100%', boxSizing: 'border-box', padding: '20px',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}>
            <div style={{
              flex: '1 1 0%', minHeight: 0, width: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{
                height: '65%', maxWidth: '65%', aspectRatio: '1 / 1', borderRadius: '50%',
                backgroundColor: '#28a745',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 0 30px rgba(40, 167, 69, 0.3)'
              }}>
                <svg width="40%" height="40%" viewBox="0 0 24 24" fill="none" stroke="white"
                  strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
            </div>
            <p style={{ marginTop: '12px', fontSize: '18px', color: '#28a745', textAlign: 'center', fontWeight: 'bold' }}>
              Recording Complete
            </p>
          </div>
        </MediaWindow>
      )}

      <div className="actions w-full flex justify-center">
        {!isPhoneMode && !isRecording && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
            <label
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                cursor: toggleDisabled ? 'not-allowed' : 'pointer',
                opacity: toggleDisabled ? 0.5 : 1, userSelect: 'none',
              }}
            >
              <span style={{ fontSize: 14, color: '#495057' }}>Record video</span>
              <span style={{ position: 'relative', width: 42, height: 24, display: 'inline-block' }}>
                <input
                  type="checkbox"
                  checked={videoRecordingEnabled}
                  disabled={toggleDisabled}
                  onChange={() => onVideoRecordingEnabledChange?.(!videoRecordingEnabled)}
                  style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    margin: 0, opacity: 0, cursor: toggleDisabled ? 'not-allowed' : 'pointer',
                  }}
                />
                <span
                  style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 12,
                    background: videoRecordingEnabled ? '#28a745' : '#adb5bd',
                    transition: 'background 150ms ease', pointerEvents: 'none',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute', top: 2, left: videoRecordingEnabled ? 20 : 2,
                      width: 20, height: 20, borderRadius: '50%', background: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'left 150ms ease',
                    }}
                  />
                </span>
              </span>
            </label>
          </div>
        )}
        <p style={{ textAlign: "center" }}>
          Record your response and submit for analysis:
        </p>

        {/* Actionable failure message — the core UX fix for silent captures */}
        {(captureError || streamError) && (
          <div style={{
            width: '100%', maxWidth: 600, margin: '0 auto 10px', padding: 12,
            background: '#f8d7da', color: '#721c24', borderRadius: 6, textAlign: 'center'
          }}>
            <div>{captureError ?? streamError}</div>
            <div style={{ marginTop: 8, fontSize: 14, color: '#721c24' }}>
              You can record again, or test your microphone to see what's wrong.
            </div>
            <Button color="secondary" size="sm" style={{ marginTop: 8 }} onClick={openMicTest}>
              Test my microphone
            </Button>
          </div>
        )}

        {!audioRecorder && !audioBlob && (
          <Button style={{ marginBottom: "10px", marginTop: "10px", color: "white" }}
            block color={"info"} onClick={startRecording} className="button">
            Start Recording
          </Button>
        )}
        {audioRecorder && (
          <Button style={{ marginBottom: "10px", marginTop: "10px", color: "white" }}
            block color={"info"} onClick={stopRecording} className="button">
            Stop Recording...
          </Button>
        )}
        {audioBlob && !audioRecorder && (
          <div className="w-full flex flex-col gap-2">
            <Button style={{ marginBottom: "10px", marginTop: "10px", color: "white" }}
              block
              color={fileProcessingStatus === 'error' ? "danger" : audioTooLong ? "danger" : "info"}
              onClick={handleSubmit} className="button" disabled={!canAnalyze}>
              {getAnalyzeButtonText()}
            </Button>
            <Button style={{ marginBottom: "10px", color: "white" }}
              block color={"secondary"} onClick={startRecording} className="button"
              disabled={fileProcessingStatus === 'uploading' || fileProcessingStatus === 'verifying'}>
              Record Again
            </Button>
          </div>
        )}
      </div>

      <MicTestModal isOpen={micTestOpen} onClose={closeMicTest} supportEmail={SUPPORT_EMAIL} />

    </div>
  );
};

export default RecordScreen;